import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { PlusSquare, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';

import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';

import {
  checkIsExpire,
  GetFilterUrl,
  productOptions,
  toFixedThree,
} from '@/utility/helper';

import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';

import PageHeading from '@/components/common/PageHeading';
import CommonFilter from '@/components/common/CommonFilter';
import Pagination from '@/components/common/Pagination';
import ImagePreview from '@/components/common/media/ImagePreview';

import { RoutesLink } from '@/utility/RouteLinks';
import { usePermissions } from '@/hooks/usePermissions';
import { useListQueryParams } from '@/hooks/useListQueryParams';

import {
  ICategoryData,
  ICategoryResponse,
} from '@/interfaces/categoryResponse.interface';

import {
  IBrandsResponse,
  IBrandsResponseList,
} from '@/interfaces/brandsResponse.interface';
import StatusDropdown from '@/components/common/StatusComponent';

interface PageState {
  isLoading: boolean;
  products: IproductResponse[];
  categories: ICategoryData[];
  brands: IBrandsResponse[];
  pageInfo: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  showDeleteModel: boolean;
  selectedId: string | null;
}

const DiscountedProducts: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions();

  const { params, setParams } = useListQueryParams({
    search: '',
    filterList: 'products',
  });

  const { page, limit, search, filterList } = params;

  const [state, setState] = useState<PageState>({
    isLoading: false,
    products: [],
    categories: [],
    brands: [],
    pageInfo: { page, limit, total: 0, totalPages: 0 },
    showDeleteModel: false,
    selectedId: null,
  });

  const setPartialState = (payload: Partial<PageState>) =>
    setState((prev) => ({ ...prev, ...payload }));

  /* ================================
      FETCHERS
  ================================= */

  const fetchProducts = async () => {
    setPartialState({ isLoading: true });
    const query = GetFilterUrl({
      page,
      limit,
      search,
      status: 'active',
      minDiscount: 1,
    });

    const res = await callApi<IproductResponseList>(
      `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query}`,
      { method: 'GET' },
    );

    setPartialState({
      products: res.data,
      pageInfo: res.pageInfo,
      isLoading: false,
    });
  };

  const fetchCategories = async () => {
    setPartialState({ isLoading: true });
    const query = GetFilterUrl({
      page,
      limit,
      search,
      status: 'active',
      hasDiscount: true,
    });

    const res = await callApi<ICategoryResponse>(
      `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}?${query}`,
      { method: 'GET' },
    );

    setPartialState({
      categories: res.data,
      pageInfo: res.pageInfo,
      isLoading: false,
    });
  };

  const fetchBrands = async () => {
    setPartialState({ isLoading: true });
    const query = GetFilterUrl({
      page,
      limit,
      search,
      status: 'active',
      hasDiscount: true,
    });

    const res = await callApi<IBrandsResponseList>(
      `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}?${query}`,
      { method: 'GET' },
    );

    setPartialState({
      brands: res.data,
      pageInfo: res.pageInfo,
      isLoading: false,
    });
  };

  /* ================================
      TAB BASED FETCH
  ================================= */

  useEffect(() => {
    if (filterList === 'products') fetchProducts();
    if (filterList === 'categories') fetchCategories();
    if (filterList === 'brands') fetchBrands();
  }, [filterList, page, limit, search]);

  /* ================================
      COLUMNS
  ================================= */

  const productColumns: TableColumn<IproductResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${
            item.directImageUrl || item.media?.[0]?.url
          }`}
          text={item.name}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={productOptions}
          onChange={() => {}}
        />
      ),
    },
    {
      key: 'stock',
      header: 'Unit Stock',
      render: (item) => item.productInfo[0]?.secondaryQuantity || 0,
    },
    {
      key: 'ctnStock',
      header: 'CTN Stock',
      render: (item) => item.productInfo[0]?.primaryQuantity || 0,
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (item) =>
        checkIsExpire(item.productInfo[0]?.discountEndDate) ? 'Yes' : 'No',
    },
    {
      key: 'rating',
      header: 'Rating',
      render: (item) => (
        <div className="flex items-center gap-1">
          {toFixedThree(item.averageRating || 0, 1)}
          <Star size={16} className="text-orange-400 fill-orange-500" />
        </div>
      ),
    },
  ];

  const categoryColumns: TableColumn<ICategoryData>[] = [
    {
      key: 'product',
      header: 'Category',
      render: (item) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${item.media?.[0]?.url}`}
          text={item.name}
        />
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (item) => (
        <span className="inline-block max-w-[250px] truncate">
          {item.description}
        </span>
      ),
    },
    {
      key: 'productCount',
      header: 'Products',
    },
  ];

  const brandColumns: TableColumn<IBrandsResponse>[] = [
    {
      key: 'brand',
      header: 'Brand',
      render: (i) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${i.media?.[0]?.url}`}
          text={i.name}
        />
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (item) => (
        <span className="inline-block max-w-[250px] truncate">
          {item.description}
        </span>
      ),
    },
    { key: 'productCount', header: 'Products' },
  ];

  /* ================================
      ACTIONS
  ================================= */

  const productActions = useMemo(
    () =>
      [
        {
          text: 'View',
          onClick: (p: IproductResponse) =>
            navigate(`${RoutesLink.productDetail}/${p.id}`),
        },
        canEdit('product') && {
          text: 'Edit',
          onClick: (p: IproductResponse) =>
            navigate(`${RoutesLink.addProducts}/${p.id}`),
        },
      ].filter(Boolean),
    [canEdit],
  );

  const categoryActions = useMemo(
    () =>
      [
        canEdit('category') && {
          text: 'Edit',
          onClick: (c: ICategoryData) =>
            navigate(`${RoutesLink.addCategory}/${c.id}`),
        },
      ].filter(Boolean),
    [canEdit],
  );

  const brandActions = useMemo(
    () =>
      [
        canEdit('brand') && {
          text: 'Edit',
          onClick: (b: IBrandsResponse) =>
            navigate(`${RoutesLink.addBrands}/${b.id}`),
        },
      ].filter(Boolean),
    [canEdit, canDelete],
  );

  return (
    <Layout>
      <PageHeading
        text="Discounted Items"
        button={
          canCreate('discount')
            ? {
                icon: <PlusSquare />,
                text: 'Add Discount',
                onClick: () => navigate(RoutesLink.addDiscount),
              }
            : undefined
        }
      />

      <Card>
        <CommonFilter
          activeFilter={filterList}
          filters={[
            { label: 'Products', value: 'products' },
            { label: 'Categories', value: 'categories' },
            { label: 'Brands', value: 'brands' },
          ]}
          onSearch={(t) => setParams({ search: t, page: 1 })}
          onChange={(f) => setParams({ filterList: f, page: 1 })}
        />

        {filterList === 'products' && (
          <CommonTable
            columns={productColumns}
            data={state.products}
            actions={productActions as any}
            loading={state.isLoading}
            getItemId={(i) => i.id}
          />
        )}

        {filterList === 'categories' && (
          <CommonTable
            columns={categoryColumns}
            data={state.categories}
            actions={categoryActions as any}
            loading={state.isLoading}
            getItemId={(i) => i.id}
          />
        )}

        {filterList === 'brands' && (
          <CommonTable
            columns={brandColumns}
            data={state.brands}
            actions={brandActions as any}
            loading={state.isLoading}
            getItemId={(i) => i.id}
          />
        )}

        <Pagination
          paginationData={state.pageInfo}
          onPageChange={(p) => setParams({ page: p })}
          onPageSizeChange={(l) => setParams({ limit: l })}
        />
      </Card>
    </Layout>
  );
};

export default DiscountedProducts;
